import React, {
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { Layout as CmsLayout, HeroSearch } from 'api/cms';
import { Subsite } from 'api/website';
import { ArticleSearchHero } from 'components/ArticleSearchHero';
import { BannerAds } from 'components/BannerAds';
import Box from 'components/Box';
import { Button } from 'components/Button';
import Icon from 'components/Icon/Icon';
import PageFooter from 'components/Page/PageFooter';
import PageHeader from 'components/Page/PageHeader';
import PrintHidden from 'components/PrintHidden';
import { SearchHero } from 'components/Search/SearchHero';
import SearchMenuComponent from 'components/Search/SearchMenu';
import { H4 } from 'components/Text';
import HideContainer from 'containers/HideContainer';
import NoAmp from 'containers/NoAmp';
import ScrollWatcherContainer from 'containers/ScrollWatcherContainer';
import BaseLayout from 'layouts/BaseLayout';
import { setWithSearchHero as reduxSetWithSearchHero } from 'store/main/ducks/layout';
import { colors, gutterWidth, shadows, transition } from 'theme';
import AmpContext from 'utils/AmpContext';
import { trackEvent } from 'utils/analytics';
import { isBrowser } from 'utils/browser';
import { deleteCookie, getListCookie } from 'utils/cookies';
import { isDefined } from 'utils/functional';
import { getItem } from 'utils/localStorage';

const testMode = getItem('testMode');
const StyledStickyBox = styled(Box)`
  ${!testMode &&
  `
    transform: translateZ(0);
    position: sticky;
    top: 0;
    z-index: 100;
    width: 100%;
  `}
`;
const SearchMenuContainer = styled(Box)`
  @media print {
    display: none;
  }
`;

const PageHeaderContainer = styled.div.attrs<{ exposeSiteNav?: boolean }>(
  (props) => ({
    style: {
      width: '100%',
      left: 0,
      top: 0,
      position: props.exposeSiteNav ? 'fixed' : 'relative',
      zIndex: props.exposeSiteNav ? 9999 : 102,
      boxShadow: props.exposeSiteNav ? '0 2px 4px rgba(0,0,0,0.15)' : null,
      pointerEvents: 'none',
    },
  })
)`
  /* Don't remove this string section, this component will stop working */
`;

type OwnProps = {
  noAds?: boolean;
  buttonBar?: ReactNode;
  onHomePage?: boolean;
  onCms?: boolean;
  onStaffDashboard?: boolean;
  localeUrl?: Record<string, string>;
  navSection?: 'MAIN' | 'STAFF';
  defaultWithSearchHero?: boolean;
  layout?: CmsLayout;
  searchHeroHasCta?: boolean;
  children?: ReactNode;
  searchMode?: boolean;
  minimalNav?: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setUserInitiatedSearch?: (...args: Array<any>) => any;
  defaultSearchType?: DefaultSearchType;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSearch?: (...args: Array<any>) => any;
  postSearchToolbar?: ReactNode;
  darkMode?: boolean;
  showListingCounts?: boolean;
  hideFooterDivider?: boolean;
  toolbar?: ReactNode;
  staffTools?: ReactNode | null | undefined;
  hero?: HeroSearch;
  exposeSiteNav?: boolean;
  content?: Record<string, unknown>;
  subsite?: Subsite;
};
type Props = OwnProps & {
  withSearchHero: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setWithSearchHero: (...args: Array<any>) => any;
  // TODO: Fix this the next time the file is edited.
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const Layout = ({
  noAds,
  buttonBar,
  content,
  subsite,
  localeUrl,
  navSection = 'MAIN',
  defaultWithSearchHero,
  layout,
  searchHeroHasCta,
  children,
  setUserInitiatedSearch,
  defaultSearchType,
  onSearch,
  minimalNav,
  postSearchToolbar,
  darkMode,
  showListingCounts,
  hideFooterDivider,
  toolbar,
  staffTools,
  searchMode,
  hero,
  withSearchHero,
  setWithSearchHero,
  exposeSiteNav,
  ...props
}: Props) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [pageUrlIsSet, setPageUrlIsSet] = useState(false);
  const [pageUrl, setPageUrl] = useState({
    en: '',
    es: '',
    pt: '',
  });

  const heroRef = useRef(null);
  const requestIsAmp = useContext(AmpContext);
  const [trackedScrollBottom, setTrackedScrollBottom] = useState(false);
  const queuedTrackers = getListCookie('trackEvents');
  useEffect(() => {
    queuedTrackers.forEach((e) => trackEvent(e.type));
    deleteCookie('trackEvents');
  }, [queuedTrackers]);
  useEffect(() => {
    if (
      isDefined(defaultWithSearchHero) &&
      defaultWithSearchHero !== withSearchHero
    ) {
      setWithSearchHero(defaultWithSearchHero);
    }
  }, [setWithSearchHero, defaultWithSearchHero, withSearchHero]);
  useEffect(() => {
    if (isBrowser() && !pageUrlIsSet) {
      const urlToSet = localeUrl || {
        en: window.location.pathname,
        es: window.location.pathname,
        pt: window.location.pathname,
      };
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'Record<string, any>' is not assi... Remove this comment to see the full error message
      setPageUrl(urlToSet);
      setPageUrlIsSet(true);
    }
  }, [pageUrl, pageUrlIsSet, setPageUrl, setPageUrlIsSet, localeUrl]);

  const toggleSearch = (open = !searchOpen) => {
    setSearchOpen(open);
  };

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/function-component-definition, react/no-unstable-nested-components
  const SearchMenu = ({
    height,
    onSearch: searchHandler,
  }: {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    height?: any;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSearch?: (...args: Array<any>) => any;
  }) => {
    const search = () => {
      if (onSearch) {
        onSearch();
      }

      if (searchHandler) {
        searchHandler();
      }
    };

    return (
      <SearchMenuContainer height={height}>
        <SearchMenuComponent
          searchMode={searchMode}
          navSection={navSection}
          onSearch={search}
          defaultSearchType={defaultSearchType}
          searchFrom="Header Search Bar"
          inPageHeader
        />
      </SearchMenuContainer>
    );
  };

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/function-component-definition, react/no-unstable-nested-components
  const StaffTools = () =>
    staffTools ? (
      <Box
        position="absolute"
        top="100%"
        right={gutterWidth / 2}
        mt={[gutterWidth / 2, null, null, gutterWidth + 18]}
      >
        {staffTools}
      </Box>
    ) : null;

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/function-component-definition, react/no-unstable-nested-components
  const FixedStaffTools = () =>
    staffTools ? (
      <Box
        position="fixed"
        zIndex={102}
        top={[60 + gutterWidth / 2, null, null, 190]}
        right={gutterWidth / 2}
      >
        {staffTools}
      </Box>
    ) : null;

  const renderWithSearchHero = isDefined(defaultWithSearchHero)
    ? defaultWithSearchHero
    : withSearchHero;
  const hideHeroSearch = isDefined(searchMode) && searchMode && !requestIsAmp;

  return (
    <BaseLayout
      buttonBar={buttonBar}
      stickyHeaderHeight={
        isDefined(postSearchToolbar)
          ? [135, 135, 135, 75]
          : [60, 60, 60, searchMode || !renderWithSearchHero ? 83 : null]
      }
      header={
        <>
          {!noAds ? <BannerAds /> : null}
          {/* @ts-expect-error Fix type */}
          <PageHeaderContainer exposeSiteNav={exposeSiteNav}>
            <Box>
              <PrintHidden>
                <PageHeader
                  minimalNav={minimalNav}
                  url={pageUrl}
                  navSection={navSection}
                  requestIsAmp={requestIsAmp}
                  withSearchHero={renderWithSearchHero && !exposeSiteNav}
                  // @ts-expect-error ts-migrate(2322) FIXME: Type '{ minimalNav: boolean; url: { en: string; es... Remove this comment to see the full error message
                  searchHeroHasCta={searchHeroHasCta}
                  toggleSearch={toggleSearch}
                />
              </PrintHidden>
            </Box>
          </PageHeaderContainer>
          {(!hero || searchMode) && !renderWithSearchHero ? (
            <NoAmp>
              {isDefined(postSearchToolbar) ? (
                <>
                  <HideContainer xs sm md position="relative" zIndex="101">
                    <SearchMenu />
                  </HideContainer>
                  <Box
                    component={StyledStickyBox}
                    display={['none', null, null, 'block']}
                  >
                    {postSearchToolbar}
                    <StaffTools />
                  </Box>
                  <Box height={75} display={['block', null, null, 'none']}>
                    <Box
                      position="fixed"
                      top={60}
                      left={0}
                      width={1}
                      zIndex={101}
                    >
                      {postSearchToolbar}
                      <StaffTools />
                    </Box>
                  </Box>
                </>
              ) : (
                <>
                  <FixedStaffTools />
                  <HideContainer xs sm md noWrapper>
                    <StyledStickyBox>
                      <Box boxShadow={shadows.toolbar}>
                        <SearchMenu />
                      </Box>
                    </StyledStickyBox>
                  </HideContainer>
                </>
              )}
            </NoAmp>
          ) : (
            <FixedStaffTools />
          )}
        </>
      }
    >
      {toolbar}

      {hero && (
        <Box
          position="relative"
          zIndex={2}
          height={hideHeroSearch ? 0 : 'auto'}
          overflow={hideHeroSearch && 'hidden'}
          bg={darkMode && colors.fog}
        >
          <div ref={heroRef}>
            {defaultSearchType === 'CMS_PAGE' ? (
              <ArticleSearchHero
                title={hero.text}
                image={hero.image}
                imageMobile={hero.imageMobile}
                content={content}
                subsite={subsite}
              />
            ) : (
              <SearchHero
                title={hero.text}
                image={hero.image}
                imageMobile={hero.imageMobile}
                searchMode={hideHeroSearch}
                navSection={navSection}
                onSearch={onSearch}
                defaultSearchType={defaultSearchType}
                showListingCounts={showListingCounts}
              />
            )}
          </div>
        </Box>
      )}

      <PrintHidden>
        <HideContainer lg xl>
          <Box
            position="fixed"
            top={0}
            left={0}
            zIndex={102}
            transform={
              searchOpen
                ? 'translateY(0)'
                : 'translateY(-100%) translateY(-6px)'
            }
            width="100%"
            height="100%"
            transition={`all ${transition}`}
          >
            <Box
              classes={['flex', 'align-center', 'justify-between']}
              position="absolute"
              left={0}
              top={0}
              zIndex={1}
              px={gutterWidth / 2}
              height={60}
              width="100%"
              borderBottom={`1px solid ${colors.aluminum}`}
            >
              <H4 mb={0} color={colors.waterBlue}>
                {getText('Search')}
              </H4>

              <Button
                onClick={() => setSearchOpen(false)}
                aria-label={getText('Close search')}
                ml="auto"
              >
                <Icon
                  name="close"
                  display="block"
                  width={16}
                  height={16}
                  stroke={colors.waterBlue}
                />
              </Button>
            </Box>
            <SearchMenu
              // @ts-expect-error ts-migrate(2322) FIXME: Type '{ searchMode: boolean; height: string; onSea... Remove this comment to see the full error message
              searchMode={searchMode}
              height="100%"
              onSearch={() => setSearchOpen(false)}
            />
          </Box>
        </HideContainer>
      </PrintHidden>

      <Box
        position="relative"
        flex="1 1 auto"
        bg={darkMode && colors.fog}
        pb={[36, null, null, 54]}
        mb={darkMode && '-1px'}
        borderBottom={darkMode && `1px solid ${colors.aluminum}`}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        {children}
      </Box>
      <PrintHidden>
        <ScrollWatcherContainer>
          {({ isInViewport }) => {
            if (isInViewport && !trackedScrollBottom) {
              trackEvent('Scroll: Bottom of Page', {
                reached_through: 'Scrolling',
              });
              setTrackedScrollBottom(true);
            }

            return <PageFooter url={pageUrl} hideDivider={hideFooterDivider} />;
          }}
        </ScrollWatcherContainer>
      </PrintHidden>
    </BaseLayout>
  );
};

const mapStateToProps = ({ layout }) => ({
  withSearchHero: layout.withSearchHero,
});

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, {
  setWithSearchHero: reduxSetWithSearchHero,
})(Layout);
